import { parseComposition, prepareRevision, type CompositionDocument } from './document';

const DATABASE = 'museflow-compositions-v1';
const HISTORY_LIMIT = 12;

export function openCompositionDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 3);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('documents'))
        db.createObjectStore('documents', { keyPath: ['ownerId', 'id'] }).createIndex(
          'owner',
          'ownerId',
        );
      if (!db.objectStoreNames.contains('history'))
        db.createObjectStore('history', { keyPath: ['ownerId', 'id', 'revision'] }).createIndex(
          'document',
          ['ownerId', 'id'],
        );
      if (!db.objectStoreNames.contains('outbox'))
        db.createObjectStore('outbox', { keyPath: ['ownerId', 'id'] });
      // Retained take recordings are kept beside the documents but never inside them,
      // so a score sync can never carry a musician's room audio to the cloud.
      if (!db.objectStoreNames.contains('assets')) {
        const assets = db.createObjectStore('assets', { keyPath: ['ownerId', 'id'] });
        assets.createIndex('owner', 'ownerId');
        assets.createIndex('composition', ['ownerId', 'compositionId']);
      }
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
    request.onerror = () => reject(request.error);
    request.onblocked = () =>
      reject(new Error('Close the other MuseFlow tab to finish opening composition storage.'));
  });
}

export async function listCompositions(ownerId: string): Promise<CompositionDocument[]> {
  const db = await openCompositionDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction('documents', 'readonly');
      const request = transaction.objectStore('documents').index('owner').getAll(ownerId);
      transaction.oncomplete = () => {
        try {
          resolve(
            request.result
              .map(parseComposition)
              .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
          );
        } catch (error) {
          reject(error);
        }
      };
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () =>
        reject(transaction.error ?? new Error('Composition storage was interrupted.'));
    });
  } finally {
    db.close();
  }
}

/** Expected-revision check, current document and recovery journal commit atomically. */
export async function saveComposition(document: CompositionDocument): Promise<CompositionDocument> {
  parseComposition(document);
  const db = await openCompositionDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(['documents', 'history'], 'readwrite');
      const documents = transaction.objectStore('documents');
      const history = transaction.objectStore('history');
      const request = documents.get([document.ownerId, document.id]);
      let saved: CompositionDocument;
      let saveError: unknown;
      request.onsuccess = () => {
        try {
          saved = prepareRevision(document, request.result);
          documents.put(saved);
          history.put(saved);
          const versions = history.index('document').getAllKeys([saved.ownerId, saved.id]);
          versions.onsuccess = () =>
            versions.result.slice(0, -HISTORY_LIMIT).forEach((key) => history.delete(key));
        } catch (reason) {
          saveError = reason;
          transaction.abort();
        }
      };
      transaction.oncomplete = () => resolve(saved);
      transaction.onerror = () =>
        reject(transaction.error ?? new Error('Unable to save on this device.'));
      transaction.onabort = () =>
        reject(saveError ?? transaction.error ?? new Error('The local save was interrupted.'));
    });
  } finally {
    db.close();
  }
}

export async function getCompositionHistory(
  ownerId: string,
  id: string,
): Promise<CompositionDocument[]> {
  const db = await openCompositionDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction('history', 'readonly');
      const request = transaction.objectStore('history').index('document').getAll([ownerId, id]);
      transaction.oncomplete = () => {
        try {
          resolve(request.result.map(parseComposition).sort((a, b) => b.revision - a.revision));
        } catch (reason) {
          reject(reason);
        }
      };
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () =>
        reject(transaction.error ?? new Error('Recovery history could not be read.'));
    });
  } finally {
    db.close();
  }
}
